import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Calendar, 
  Check, 
  X, 
  RefreshCw, 
  Link2, 
  Clock,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  ExternalLink
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const MicrosoftCalendarSync = () => {
  const { api, user } = useAuth();
  
  const [connectionStatus, setConnectionStatus] = useState({
    configured: false,
    connected: false,
    connection: null
  });
  const [suggestions, setSuggestions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [processing, setProcessing] = useState({});
  const [isOpen, setIsOpen] = useState(true);
  const [selectedProject, setSelectedProject] = useState('');

  // Fetch connection status and suggestions
  const fetchData = async () => {
    if (!api) return;
    try {
      const [statusRes, suggestionsRes, projectsRes] = await Promise.all([
        api.get('/microsoft/status'),
        api.get('/microsoft/suggestions?status=pending').catch(() => ({ data: [] })),
        api.get('/projects')
      ]);
      
      setConnectionStatus(statusRes.data);
      setSuggestions(suggestionsRes.data || []);
      setProjects(projectsRes.data || []);
      
      // Set default project
      if (user?.default_project_id) {
        setSelectedProject(user.default_project_id);
      }
    } catch (error) {
      console.error('Error fetching Microsoft data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Check for callback messages in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('microsoft_connected') === 'true') {
      toast.success('Microsoft account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('microsoft_error')) {
      toast.error(`Microsoft connection failed: ${params.get('microsoft_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [api, user]);

  // Connect Microsoft account
  const handleConnect = async () => {
    try {
      const response = await api.get('/microsoft/auth/url');
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else if (!response.data.configured) {
        toast.error('Microsoft integration not configured. Please contact your administrator.');
      }
    } catch (error) {
      toast.error('Failed to initiate connection');
    }
  };

  // Disconnect Microsoft account
  const handleDisconnect = async () => {
    try {
      await api.delete('/microsoft/disconnect');
      setConnectionStatus({ configured: true, connected: false, connection: null });
      setSuggestions([]);
      toast.success('Microsoft account disconnected');
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  // Sync calendar events
  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/microsoft/sync');
      toast.success(`Synced ${response.data.synced} new events`);
      // Refresh suggestions
      const suggestionsRes = await api.get('/microsoft/suggestions?status=pending');
      setSuggestions(suggestionsRes.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please reconnect your Microsoft account.');
        setConnectionStatus(prev => ({ ...prev, connected: false }));
      } else {
        toast.error('Failed to sync calendar');
      }
    } finally {
      setSyncing(false);
    }
  };

  // Accept a suggestion
  const handleAccept = async (suggestion) => {
    setProcessing(prev => ({ ...prev, [suggestion.id]: 'accepting' }));
    try {
      await api.post(`/microsoft/suggestions/${suggestion.id}/accept`, {
        project_id: selectedProject || suggestion.project_id
      });
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      toast.success('Time entry created');
    } catch (error) {
      toast.error('Failed to accept suggestion');
    } finally {
      setProcessing(prev => ({ ...prev, [suggestion.id]: null }));
    }
  };

  // Reject a suggestion
  const handleReject = async (suggestion) => {
    setProcessing(prev => ({ ...prev, [suggestion.id]: 'rejecting' }));
    try {
      await api.post(`/microsoft/suggestions/${suggestion.id}/reject`);
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      toast.success('Suggestion dismissed');
    } catch (error) {
      toast.error('Failed to dismiss suggestion');
    } finally {
      setProcessing(prev => ({ ...prev, [suggestion.id]: null }));
    }
  };

  // Bulk accept all
  const handleAcceptAll = async () => {
    if (suggestions.length === 0) return;
    
    setProcessing({ bulk: 'accepting' });
    try {
      const response = await api.post('/microsoft/suggestions/bulk-accept', {
        suggestion_ids: suggestions.map(s => s.id),
        project_id: selectedProject
      });
      toast.success(`Created ${response.data.accepted} time entries`);
      setSuggestions([]);
    } catch (error) {
      toast.error('Failed to accept suggestions');
    } finally {
      setProcessing({});
    }
  };

  // Bulk reject all
  const handleRejectAll = async () => {
    if (suggestions.length === 0) return;
    
    setProcessing({ bulk: 'rejecting' });
    try {
      await api.post('/microsoft/suggestions/bulk-reject', {
        suggestion_ids: suggestions.map(s => s.id)
      });
      toast.success('All suggestions dismissed');
      setSuggestions([]);
    } catch (error) {
      toast.error('Failed to dismiss suggestions');
    } finally {
      setProcessing({});
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  // Format date/time
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="premium-card p-6">
        <div className="flex items-center justify-center h-20">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="premium-card" data-testid="microsoft-calendar-section">
        <CollapsibleTrigger className="w-full p-6 flex items-center justify-between cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-blue-600" />
            </div>
            <div className="text-left">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                Microsoft Calendar Sync
              </h3>
              <p className="text-xs text-slate-500">
                {connectionStatus.connected 
                  ? `Connected as ${connectionStatus.connection?.email}` 
                  : 'Import meetings as time entries'}
              </p>
            </div>
            {suggestions.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 rounded-full">
                {suggestions.length} pending
              </span>
            )}
          </div>
          {isOpen ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-6 pb-6 space-y-4">
            {/* Connection Status */}
            {!connectionStatus.configured ? (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/10 rounded-xl text-center">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Microsoft integration is not configured. Please contact your administrator.
                </p>
              </div>
            ) : !connectionStatus.connected ? (
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-700 flex items-center justify-center shadow-sm">
                    <svg className="w-6 h-6" viewBox="0 0 24 24">
                      <path fill="#0078D4" d="M11.5 2a9.5 9.5 0 109.5 9.5A9.51 9.51 0 0011.5 2zm0 17a7.5 7.5 0 117.5-7.5 7.508 7.508 0 01-7.5 7.5z"/>
                      <path fill="#0078D4" d="M11.5 6a5.5 5.5 0 105.5 5.5A5.506 5.506 0 0011.5 6zm0 9a3.5 3.5 0 113.5-3.5 3.504 3.504 0 01-3.5 3.5z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      Connect your Microsoft account
                    </p>
                    <p className="text-xs text-slate-500">
                      Import accepted meetings as time entries
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleConnect}
                  className="h-9 rounded-xl bg-[#0078D4] hover:bg-[#006CBE]"
                  data-testid="connect-microsoft-btn"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect
                </Button>
              </div>
            ) : (
              <>
                {/* Connected Status & Actions */}
                <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                        Connected to Microsoft
                      </p>
                      <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">
                        {connectionStatus.connection?.email}
                        {connectionStatus.connection?.last_sync_at && (
                          <> · Last sync: {new Date(connectionStatus.connection.last_sync_at).toLocaleString()}</>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSync}
                      disabled={syncing}
                      className="h-8 rounded-lg"
                      data-testid="sync-calendar-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                      {syncing ? 'Syncing...' : 'Sync Now'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDisconnect}
                      className="h-8 text-xs text-slate-500 hover:text-red-600"
                      data-testid="disconnect-microsoft-btn"
                    >
                      Disconnect
                    </Button>
                  </div>
                </div>

                {/* Pending Suggestions */}
                {suggestions.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        Pending Calendar Events ({suggestions.length})
                      </h4>
                      <div className="flex items-center gap-2">
                        <Select value={selectedProject} onValueChange={setSelectedProject}>
                          <SelectTrigger className="h-8 w-[180px] text-xs rounded-lg">
                            <SelectValue placeholder="Select project" />
                          </SelectTrigger>
                          <SelectContent>
                            {projects.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-2 h-2 rounded-full" 
                                    style={{ backgroundColor: p.color || '#3B82F6' }}
                                  />
                                  {p.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAcceptAll}
                          disabled={processing.bulk}
                          className="h-8 text-xs rounded-lg text-emerald-600 border-emerald-200 hover:bg-emerald-50 dark:bg-emerald-900/30"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Accept All
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleRejectAll}
                          disabled={processing.bulk}
                          className="h-8 text-xs text-slate-500 hover:text-red-600"
                        >
                          Dismiss All
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {suggestions.map(suggestion => (
                        <div 
                          key={suggestion.id}
                          className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl group"
                          data-testid={`suggestion-${suggestion.id}`}
                        >
                          <div className="flex-1 min-w-0 mr-4">
                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                              {suggestion.title}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDuration(suggestion.duration)}
                              </span>
                              <span>{formatDateTime(suggestion.start_time)}</span>
                              {suggestion.location && (
                                <span className="truncate max-w-[150px]">{suggestion.location}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAccept(suggestion)}
                              disabled={processing[suggestion.id]}
                              className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-50 dark:bg-emerald-900/10"
                              data-testid={`accept-${suggestion.id}`}
                            >
                              {processing[suggestion.id] === 'accepting' ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReject(suggestion)}
                              disabled={processing[suggestion.id]}
                              className="h-8 w-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-50 dark:bg-red-900/10"
                              data-testid={`reject-${suggestion.id}`}
                            >
                              {processing[suggestion.id] === 'rejecting' ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {suggestions.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">No pending calendar events</p>
                    <p className="text-xs mt-1">Click "Sync Now" to fetch your latest meetings</p>
                  </div>
                )}
              </>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default MicrosoftCalendarSync;
